import  { useEffect } from 'react';
import { useMapBox } from './BalloonEventsMapBox';
import { useBalloonEventsOptions } from '../contexts/EmbedOptions';
import { cityCoordinates } from '../constants/polygon';

export default function PolygonHighlight({

}) {
  const mapContext = useMapBox();
  const { showPolygon } = useBalloonEventsOptions();

  // Use embed options or fallback to props or defaults
  const citiesToShow = showPolygon ?? [];
  const finalFillOpacity = 1;
  const finalStrokeWidth = 2;

  // Color mapping for each city
  const cityColors: Record<string, string> = {
    linz: '#BF2088',    // Magenta/Pink for Linz
    molln: '#FF6B35',   // Orange for Molln  
    steyr: '#4ECDC4'    // Teal for Steyr
  };

  // Debug logging
  console.log('PolygonHighlight component loaded:', {
    mapContext,
    map: !!mapContext?.map,
    showPolygon,
    citiesToShow,
    cityColors,
    finalFillOpacity
  });

  // Add a useEffect to wait for map to become available
  useEffect(() => {
    if (mapContext?.map) {
      console.log('Map became available, triggering polygon rendering');
    }
  }, [mapContext?.map]);

  // Main useEffect for polygon rendering - always called
  useEffect(() => {
    // Early return if map is not available or no cities to show
    if (!mapContext?.map || citiesToShow.length === 0) {
      console.log('useEffect: map or cities not ready:', {
        map: !!mapContext?.map,
        citiesToShow
      });
      return;
    }

    const map = mapContext.map;
    console.log('useEffect: Starting polygon rendering with map:', !!map, 'cities:', citiesToShow);

    const sourceId = 'cities-polygon';
    const strokeLayerId = 'cities-polygon-stroke';

    // Function to add polygon layers
    const addPolygonLayers = () => {
      console.log('addPolygonLayers called, style loaded:', map.isStyleLoaded());
      if (!map.isStyleLoaded()) return;

      // Remove existing layers if they exist
      if (map.getSource(sourceId)) {
        if (map.getLayer(strokeLayerId)) map.removeLayer(strokeLayerId);
        map.removeSource(sourceId);
      }

      // Create features for all requested cities
      const features = citiesToShow
        .filter(city => cityCoordinates[city])
        .map(city => ({
          type: 'Feature' as const,
          geometry: {
            type: 'Polygon' as const,
            coordinates: [cityCoordinates[city]]
          },
          properties: { 
            city,
            color: cityColors[city] || '#BF2088' // Default color if not found
          }
        }));

      if (features.length === 0) {
        console.log('No valid cities found to render');
        return;
      }

      // Create GeoJSON data for all cities
      const geojsonData = {
        type: 'FeatureCollection' as const,
        features
      };

      // Add source
      map.addSource(sourceId, {
        type: 'geojson',
        data: geojsonData
      });
      console.log('MapBox source added:', sourceId, 'with', features.length, 'cities');

      // Add stroke layer with color based on city property
      map.addLayer({
        id: strokeLayerId,
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': ['get', 'color'], // Use the color property from each feature
          'line-width': finalStrokeWidth
        }
      });
      console.log('MapBox stroke layer added:', strokeLayerId, 'with dynamic colors');
    };

    // Add error handling
    const errorHandler = (e: any) => {
      console.error('MapBox error in PolygonHighlight:', e);
    };
    map.on('error', errorHandler);

    // Check style loading status and add layers
    console.log('Map style loaded:', map.isStyleLoaded());
    console.log('Map loaded:', map.isSourceLoaded ? map.isSourceLoaded('mapbox') : 'isSourceLoaded not available');
    
    if (map.isStyleLoaded()) {
      console.log('Map style already loaded, adding layers immediately');
      addPolygonLayers();
      // Cleanup for the style-already-loaded path
      return () => {
        map.off('error', errorHandler);
        try {
          if (map.getLayer(strokeLayerId)) {
            map.removeLayer(strokeLayerId);
            map.removeSource(sourceId);
          }
        } catch {
          // Map may already be destroyed
        }
      };
    } else {
      console.log('Map style not loaded, waiting for style.load event');
      // Otherwise, wait for the style to load
      map.on('style.load', addPolygonLayers);
      
      // Also try to add layers after a delay in case style.load doesn't fire
      const fallbackTimeout = setTimeout(() => {
        console.log('Timeout check - Map style loaded:', map.isStyleLoaded());
        if (map.isStyleLoaded()) {
          console.log('Adding layers via timeout');
          addPolygonLayers();
        }
      }, 2000);

      // Store for cleanup
      return () => {
        clearTimeout(fallbackTimeout);
        map.off('style.load', addPolygonLayers);
        map.off('error', errorHandler);
        try {
          if (map.getLayer(strokeLayerId)) {
            map.removeLayer(strokeLayerId);
            map.removeSource(sourceId);
          }
        } catch {
          // Map may already be destroyed
        }
      };
    }
  }, [mapContext?.map, citiesToShow, finalFillOpacity, finalStrokeWidth]);

  return null; // This component doesn't render anything visible
}